import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AssetService from '../Services/assets.service';
import AnnualReportService from '../Services/annualReport.service';
import ProfitAndLossService from '../Services/profitAndLoss.service';
import { EXPENSE_ADJUSTMENTS_TRANSFERRED_EVENT } from '../Services/expenseAdjustments.service';
import { buildCapitalGainResult } from './capitalGainCalculations';
import { loadCapitalGainInputs, runWithConcurrency } from './capitalGainLoader';
import { getSnapshotAssetId } from './capitalGainSnapshot';
import { isAssetSoldInYear } from './assetSaleDisplayUtils';
import {
    buildSoldAssetRelationMaps,
    getSoldAssetsForDisplay,
    getTopLevelSoldAssets,
} from './soldAssetsTableUtils';

dayjs.extend(utc);

/** The matching-report subtraction IncomeTaxReconciliationService uses as the base for מס רווח הון. */
export const CAPITAL_GAIN_MATCHING_KEY = 'רווח הון';
const SUBTRACT_TYPE = 1;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const isUserSubtraction = (report) => report?.type === SUBTRACT_TYPE && !report.isInternalKey;

/** The year's 'רווח הון' row: the assets that were not transferred one by one. */
export function findCapitalGainMatchingItem(reports = []) {
    return (reports || []).find((report) => isUserSubtraction(report) && report.key === CAPITAL_GAIN_MATCHING_KEY);
}

/** Matching key of one asset's own row, e.g. "רווח הון - 45 limbo". */
export function buildCapitalGainItemKey(asset) {
    const label = [asset?.assetNumber, asset?.name]
        .map((part) => (part == null ? '' : String(part).trim()))
        .filter(Boolean)
        .join(' ');
    return label ? `${CAPITAL_GAIN_MATCHING_KEY} - ${label}` : CAPITAL_GAIN_MATCHING_KEY;
}

/** Rows transferred per asset, by key. */
export function getCapitalGainItemRows(reports = []) {
    const prefix = `${CAPITAL_GAIN_MATCHING_KEY} - `;
    const map = {};
    (reports || []).forEach((report) => {
        if (isUserSubtraction(report) && String(report.key || '').startsWith(prefix)) map[report.key] = report;
    });
    return map;
}

export function findCapitalGainItemRow(reports, asset) {
    return getCapitalGainItemRows(reports)[buildCapitalGainItemKey(asset)] || null;
}

export function formatMatchingAmount(amount) {
    return (Math.round((Number(amount) || 0) * 100) / 100).toFixed(2);
}

/**
 * Sold root assets of the year. Children sold with their root are part of its calculation, and assets
 * shown as sold only through a parent ("שייך לנכס …") have no sale of their own.
 */
export function getCapitalGainAssetsForYear(assets, assetRelations, year) {
    const soldAssets = getSoldAssetsForDisplay(assets || [], assetRelations || []);
    const { childAssetIdSet } = buildSoldAssetRelationMaps(soldAssets, assetRelations || []);
    return getTopLevelSoldAssets(soldAssets, childAssetIdSet).filter(
        (asset) => !asset.soldViaAssetGroup && isAssetSoldInYear(asset, Number(year))
    );
}

/**
 * Total gain before tax and tax for the sold assets of a year: saved snapshots where present,
 * otherwise the live calculation with default inputs.
 */
export async function loadCapitalGainYearTotals({ caseId, token, caseType, year, matchingReports = [] }) {
    const [assetsResp, relationsResp, snapshotsResp] = await Promise.all([
        AssetService.GetAllAssets(caseId, token),
        AssetService.GetAssetRelationsByCaseId(caseId, token).catch(() => null),
        AssetService.GetCapitalGainSnapshots(caseId, year, token),
    ]);
    const itemRows = getCapitalGainItemRows(matchingReports);
    const allYearAssets = getCapitalGainAssetsForYear(assetsResp?.data, relationsResp?.data, year);
    const yearAssets = allYearAssets.filter((asset) => !itemRows[buildCapitalGainItemKey(asset)]);
    const yearAssetIds = new Set(yearAssets.map((asset) => asset.id));
    const snapshots = {};
    (snapshotsResp?.data || []).forEach((snapshot) => {
        const assetId = getSnapshotAssetId(snapshot);
        if (yearAssetIds.has(assetId)) snapshots[assetId] = snapshot;
    });

    const toCompute = yearAssets.filter((asset) => !snapshots[asset.id]);
    const computed = await runWithConcurrency(toCompute, 3, async (asset) => {
        const inputs = await loadCapitalGainInputs({ caseId, assetId: asset.id, token, fallbackAsset: asset });
        const result = buildCapitalGainResult({
            basis: inputs.basis,
            cpi: inputs.cpi,
            enhancements: inputs.enhancements,
            replacements: inputs.replacements,
            caseType,
        });
        if (!result) throw new Error(inputs.cpiError || 'FailedToLoadCapitalGainData');
        return result;
    });

    let gainBeforeTax = 0;
    let totalTax = 0;
    Object.values(snapshots).forEach((snapshot) => {
        gainBeforeTax += Number(snapshot.gainBeforeTax) || 0;
        totalTax += Number(snapshot.totalTax) || 0;
    });
    let failed = 0;
    computed.forEach((outcome) => {
        if (outcome.status !== 'fulfilled') {
            failed += 1;
            return;
        }
        gainBeforeTax += Number(outcome.value.gainBeforeTax) || 0;
        totalTax += Number(outcome.value.tax.totalTax) || 0;
    });

    return {
        assetCount: yearAssets.length,
        perAssetCount: allYearAssets.length - yearAssets.length,
        savedCount: yearAssets.length - toCompute.length,
        computedCount: toCompute.length - failed,
        failedCount: failed,
        gainBeforeTax,
        totalTax,
    };
}

/** The matching-report family the annual report works in (detailed when the final P&L came from the ledger). */
export async function resolveMatchingIsDetailed(caseId, year, token) {
    try {
        const comparison = await AnnualReportService.getProfitAndLossComparison(caseId, year, false, token);
        return Boolean(comparison?.isFromProfitAndLossDetailed);
    } catch {
        return false;
    }
}

export async function loadMatchingReportsForYear({ caseId, accountantId, year, isFromProfitAndLossDetailed, token }) {
    const data = await ProfitAndLossService.GetMatchingProfitAndLossReports(
        caseId,
        accountantId,
        dayjs.utc(`${year}-01-01`).toDate(),
        dayjs.utc(`${year}-12-31`).toDate(),
        token,
        Boolean(isFromProfitAndLossDetailed),
        true
    );
    return data || [];
}

/**
 * Creates or updates a capital-gain subtraction of the year's matching report: the year row
 * ('רווח הון') or one asset's row (key from buildCapitalGainItemKey).
 * @returns {Promise<object>} the saved matching row.
 */
export async function upsertCapitalGainMatchingItem({
    caseId,
    year,
    amount,
    isFromProfitAndLossDetailed,
    existingItem = null,
    key = CAPITAL_GAIN_MATCHING_KEY,
    token,
}) {
    const existingId = existingItem?.id;
    const isSavedRow = existingId && !String(existingId).startsWith('temp_');
    const row = {
        id: isSavedRow ? existingId : EMPTY_GUID,
        caseId,
        key,
        isInternalKey: false,
        internalKey: null,
        type: SUBTRACT_TYPE,
        value: formatMatchingAmount(amount),
        isAmountPercentage: true,
        percentage: null,
        year: Number(year),
        isFromProfitAndLossDetailed: existingItem?.isFromProfitAndLossDetailed ?? Boolean(isFromProfitAndLossDetailed),
        code6111Id: existingItem?.code6111Id ?? null,
    };
    const saved = await ProfitAndLossService.SaveMatchingProfitAndLossReports([row], token);
    return saved?.[0] ?? row;
}

export async function deleteCapitalGainMatchingItem(item, token) {
    const id = item?.id;
    if (!id || String(id).startsWith('temp_') || id === EMPTY_GUID) return;
    await ProfitAndLossService.DeleteMatchingProfitAndLossReport(id, token);
}

export function notifyMatchingReportChanged(caseId, year) {
    window.dispatchEvent(
        new CustomEvent(EXPENSE_ADJUSTMENTS_TRANSFERRED_EVENT, {
            detail: { caseId, year: Number(year), source: 'capitalGain' },
        })
    );
}
